import {
  BlockItem,
  BlockTagInfo,
  TreeViewResponseItem,
} from "@/apis/preview/v2/types";
import { AxisIcon } from "@/components/table-preview/icons";
import { DimensionIcon } from "@/components/table-preview";
import { TotalPopover } from "@/components/table-preview/sidebar/tree/total-popover";
import { TagType } from "@/types/mapping";
import { Frame, Rows3, Scroll } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import ContentBox from "../../common/content-box";

const StructureIcon = ({ item }: { item: TreeViewResponseItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (item.tagType === "ABSTRACT") {
    return (
      <div className="flex p-2 items-center gap-2.5 rounded-lg bg-slate-200">
        <Frame className="w-4 h-4 text-[#0A0A0A]" />
      </div>
    );
  }
  if (item.tagType === "LINE_ITEMS") {
    return (
      <div className="flex p-2 items-center gap-2.5 rounded-lg bg-slate-200">
        <Rows3 className="w-4 h-4 text-[#0A0A0A]" />
      </div>
    );
  }
  if (item.tagType === "AXIS") {
    return (
      <AxisIcon
        className="w-[32px] h-[32px] text-[#0A0A0A] p-[8px] flex items-center flex-shrink-0 bg-slate-200 rounded-[8px]"
        isExtendedTag={item.isExtendedTag ?? false}
      />
    );
  }

  if (item.isSum) {
    return <TotalPopover isOpen={isOpen} setIsOpen={setIsOpen} item={item} />;
  }

  return (
    <DimensionIcon
      tagType={item.tagType as TagType}
      isSum={item.isSum}
      isExtendedTag={item.isExtendedTag}
    />
  );
};
interface LinkLineProps {
  linkLineLength?: number;
}

export function LinkLine({ linkLineLength = 1 }: LinkLineProps) {
  const baseHeight = 49;
  const incrementPerUnit = 64;
  const totalHeight = baseHeight + (linkLineLength - 1) * incrementPerUnit;
  const verticalLineEnd = 44 + (linkLineLength - 1) * incrementPerUnit;

  return (
    <svg
      className="absolute -left-[17px]"
      style={{ top: `${-(totalHeight - 17)}px` }}
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height={totalHeight}
      viewBox={`0 0 17 ${totalHeight}`}
      fill="none"
    >
      <path
        d={`M1 0V${verticalLineEnd}C1 ${verticalLineEnd + 2.2091} 2.79086 ${totalHeight - 1} 5 ${totalHeight - 1}H17`}
        stroke="#94A3B8"
      />
    </svg>
  );
}

function removeTagTypeFromLabel(label: string): string {
  return label.replace(/\[.*?\]/g, "").trim();
}

async function copyLabelToClipboard(label: string) {
  try {
    await navigator.clipboard.writeText(removeTagTypeFromLabel(label));
    toast.success("태그가 복사되었습니다.");
  } catch (error) {
    console.error("복사 실패:", error);
    toast.error("복사 실패: 태그를 복사하는 중 오류가 발생했습니다.");
  }
}

const StructureTagLabels = ({
  tagKey,
  ko,
  en,
}: {
  tagKey: string;
  ko: string;
  en: string;
}) => {
  return (
    <>
      <div className="flex items-center gap-2">
        <span
          className="overflow-hidden text-primary truncate w-full body-1-normal-medium cursor-pointer hover:underline hover:text-neutral-700 transition-colors"
          onClick={() => copyLabelToClipboard(ko)}
        >
          {ko}
        </span>
      </div>
      <span
        className="flex items-center gap-2 overflow-hidden text-primary truncate w-full label-1-normal-regular cursor-pointer hover:underline hover:text-neutral-700 transition-colors"
        onClick={() => copyLabelToClipboard(en)}
      >
        {en}
      </span>
    </>
  );
};

const StructureItem = ({
  item,
  linkLineLength,
}: {
  item: TreeViewResponseItem;
  linkLineLength: number;
}) => {
  return (
    <div
      className="flex items-start gap-2 self-stretch w-full"
      style={{
        paddingLeft: `${item.depth * 32}px`,
      }}
    >
      <div className="relative">
        {linkLineLength > 0 && <LinkLine linkLineLength={linkLineLength} />}
        <StructureIcon item={item} />
      </div>
      <div className="flex flex-col justify-center items-start gap-1 w-full min-w-0">
        <StructureTagLabels
          tagKey={item.tagKey}
          ko={item.tagKoLabel}
          en={item.tagEnLabel}
        />
      </div>
    </div>
  );
};

const StructureBlockItem = ({ tagKey, enLabel, koLabel }: BlockTagInfo) => {
  return (
    <div className="flex items-start gap-2 self-stretch w-full">
      <div className="relative">
        <div className="flex p-2 items-center gap-2.5 rounded-lg bg-slate-200">
          <Scroll className="w-4 h-4 text-[#0A0A0A]" />
        </div>
      </div>
      <div className="flex flex-col justify-center items-start gap-1 w-full min-w-0">
        <StructureTagLabels tagKey={tagKey} ko={koLabel} en={enLabel} />
      </div>
    </div>
  );
};

const StructureTree = ({
  items,
  dimension,
}:
  | {
      items: TreeViewResponseItem[];
      dimension: "LINE_ITEM" | "AXIS";
    }
  | {
      items: BlockItem[];
      dimension: "TEXTBLOCK";
    }) => {
  if (dimension === "TEXTBLOCK") {
    return (
      <ContentBox className="p-0 pb-5 items-start gap-2 self-stretch">
        <div className="flex w-full px-7 py-4 justify-between items-center">
          <span className="text-primary label-1-normal-medium">문장 영역</span>
        </div>
        <div className="flex flex-col px-7 py-0 items-end gap-4 self-stretch">
          {items
            .map((item) => item.tags)
            .filter((tags) => tags.length > 0)
            .map((tags) => (
              <StructureBlockItem {...tags[0]} />
            ))}
        </div>
      </ContentBox>
    );
  }
  let filteredItems: TreeViewResponseItem[] = [];
  if (dimension === "LINE_ITEM") {
    filteredItems = items;
  } else if (dimension === "AXIS") {
    filteredItems = items.filter(
      (item) => item.tagType !== "ABSTRACT" && item.tagType !== "TABLE",
    );
    filteredItems = filteredItems.map((item) => ({
      ...item,
      depth: item.depth - 2,
    }));
  }

  const linkLineLengths: number[] = [];
  const indexPerDepth: Record<number, number> = {};
  for (let i = 0; i < filteredItems.length; i++) {
    const depth = filteredItems[i].depth;
    indexPerDepth[depth] = i;
    if (depth == 0) {
      linkLineLengths.push(0);
    } else {
      linkLineLengths.push(i - indexPerDepth[depth - 1]);
    }
  }
  return (
    <ContentBox className="p-0 pb-5 items-start gap-2 self-stretch">
      <div className="flex w-full px-7 py-4 justify-between items-center">
        <span className="text-primary label-1-normal-medium">
          {dimension === "LINE_ITEM" ? "행 태그" : "열 태그"}
        </span>
      </div>
      <div className="flex flex-col px-7 py-0 items-end gap-4 self-stretch">
        {filteredItems.map((item, index) => (
          <StructureItem
            key={`${item.id}-${index}`}
            item={item}
            linkLineLength={linkLineLengths[index]}
          />
        ))}
      </div>
    </ContentBox>
  );
};

export default StructureTree;
